import React, { useState, useEffect } from 'react';
import { branding } from '../constants/branding';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import axiosInstance from '../services/axiosConfig';
import QRCode from 'qrcode';
import {
  setLoading,
  setError,
  setChildWallets,
  setSelectedChild,
  updateChildWallet,
  addChildWallet,
  createPaymentRequest,
  clearActivePaymentRequest,
  updatePaymentRequestQR,
  PaymentRequestData,
  ChildWalletData,
  deletePaymentRequest
} from '../store/slices/childWalletSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const ChildWallet: React.FC = () => {
  console.log('ChildWallet component mounted');
  
  const dispatch = useAppDispatch();
  
  // Redux state
  const { 
    childWallets, 
    selectedChild, 
    activePaymentRequest, 
    paymentRequestHistory,
    isLoading: loading, 
    error 
  } = useAppSelector((state) => state.childWallet);
  const token = useAppSelector((state) => state.auth.token);
  const user = useAppSelector((state) => state.auth.user);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const profile = useSelector((state: RootState) => state.profile.profile);
  const isChildUser = profile?.accountType === 'CHILD' || profile?.isParentAccount === false;

  // Local state for UI
  const [showAddChild, setShowAddChild] = useState(false); // Show add child modal
  const [addChildForm, setAddChildForm] = useState({
    username: '', firstName: '', lastName: '', password: '', iban: '', encryptedCardNumber: '',
    activationDate: '', dob: '', mobile: '', email: '', status: 'active', add1: '', add2: '', state: '', postcode: '', country: ''
  });
  const [addChildError, setAddChildError] = useState<string | null>(null);
  const [addChildLoading, setAddChildLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [showAddMoney, setShowAddMoney] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  
  // Payment Request QR Code states - now managed by Redux
  const [showPaymentRequest, setShowPaymentRequest] = useState(false);
  const [paymentRequestForm, setPaymentRequestForm] = useState({
    amount: '',
    description: ''
  });
  const [paymentRequestError, setPaymentRequestError] = useState<string | null>(null);

  // Fetch all child wallets (summary)
  useEffect(() => {
    console.log('ChildWallet resize useEffect triggered');
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    console.log('ChildWallet main useEffect triggered - this should fetch child wallets');
    const fetchChildWallets = async () => {
      console.log('ChildWallet useEffect triggered:', {
        token: !!token,
        isAuthenticated,
        showAddChild
      });
      
      if (!token) {
        console.log('No token available, skipping child wallet fetch');
        dispatch(setError('Authentication token not available'));
        dispatch(setLoading(false));
        return;
      }

      if (!isAuthenticated) {
        console.log('User not authenticated, skipping child wallet fetch');
        dispatch(setError('User not authenticated'));
        dispatch(setLoading(false));
        return;
      }

      try {
        console.log('Fetching child wallets...');
        dispatch(setLoading(true));
        const response = await axiosInstance.get('/child-accounts');
        console.log('Child wallets response:', response);
        
        if (response.status === 200) {
          const responseData = response.data;
          console.log('Child wallets response data:', responseData);
          
          // Check for StandardApiResponse format: { status: "success", data: [...] }
          if (responseData.status === 'success' && responseData.data) {
            const wallets = responseData.data || [];
            console.log('Setting child wallets:', wallets);
            dispatch(setChildWallets(wallets));
            // Default select first child if none selected
            if (!selectedChild && wallets.length > 0) {
              console.log('Auto-selecting first child:', wallets[0]);
              fetchChildProfile(wallets[0].childUserId);
            }
          } else {
            console.error('Failed to fetch child wallets:', responseData.message);
            dispatch(setError(responseData.message || 'Failed to fetch child wallets'));
          }
        }
      } catch (err: any) {
        console.error('Error fetching child wallets:', err);
        dispatch(setError(err.message || 'An error occurred while fetching child wallets'));
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchChildWallets();
  }, [token, isAuthenticated, showAddChild, selectedChild, dispatch]); // Added isAuthenticated and selectedChild

  // Fetch selected child profile
  const fetchChildProfile = async (childUserId: number) => {
    console.log('Fetching child profile for userId:', childUserId);
    dispatch(setLoading(true));
    dispatch(setError(null));
    if (!token) {
      console.log('No token available for child profile fetch');
      dispatch(setError('Authentication token not available'));
      dispatch(setLoading(false));
      return;
    }
    try {
      console.log('Making request to:', `/child-accounts/${childUserId}/wallet`);
      const response = await axiosInstance.get(`/child-accounts/${childUserId}/wallet`);
      console.log('Child profile response:', response);
      
      if (response.status === 200) {
        const responseData = response.data;
        console.log('Child profile response data:', responseData);
        
        // Check for StandardApiResponse format: { status: "success", data: {...} }
        if (responseData.status === 'success' && responseData.data) {
          const childData = responseData.data || {};
          console.log('Setting selected child:', childData);
          dispatch(setSelectedChild(childData));
        } else {
          console.error('Failed to fetch child wallet:', responseData.message);
          dispatch(setError(responseData.message || 'Failed to fetch child wallet'));
        }
      }
    } catch (err: any) {
      console.error('Error fetching child profile:', err);
      dispatch(setError(err.message || 'An error occurred while fetching child wallet'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Add child wallet handler
  const handleAddChild = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddChildLoading(true);
    setAddChildError(null);
    
    if (!token) {
      setAddChildError('Authentication token not available');
      setAddChildLoading(false);
      return;
    }

    console.log('Submitting child account data:', addChildForm);

    try {
      const response = await axiosInstance.post('/child-accounts', addChildForm);
      
      console.log('Child account creation response:', response);
      
      // Handle both 200 and 201 status codes for successful creation
      if (response.status === 200 || response.status === 201) {
        const responseData = response.data;
        console.log('Child account creation response data:', responseData);
        
        // Check for StandardApiResponse format: { status: "success", data: {...} }
        if (responseData.status === 'success' && responseData.data) {
          // Success - close modal and reset form
          setShowAddChild(false);
          setAddChildForm({ 
            username: '', firstName: '', lastName: '', password: '', iban: '', 
            encryptedCardNumber: '', activationDate: '', dob: '', mobile: '', 
            email: '', status: 'active', add1: '', add2: '', state: '', 
            postcode: '', country: '' 
          });
          
          // Show success message (you could add a success state here)
          console.log('Child account created successfully!');
          
          // Refresh the child wallets list
          // The useEffect will trigger due to showAddChild change
          
        } else {
          const errorMessage = responseData.message || responseData.error || 'Failed to create child account';
          console.error('Child account creation failed:', errorMessage);
          setAddChildError(errorMessage);
        }
      } else {
        console.error('Unexpected response status:', response.status);
        setAddChildError(`Unexpected response status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Error creating child account:', err);
      
      // Handle different types of errors
      if (err.response) {
        // Server responded with an error status
        const errorMessage = err.response.data?.message || 
                           err.response.data?.error || 
                           `Server error: ${err.response.status}`;
        setAddChildError(errorMessage);
        console.error('Server error response:', err.response.data);
      } else if (err.request) {
        // Request was made but no response received
        setAddChildError('Network error: Unable to reach server');
        console.error('Network error:', err.request);
      } else {
        // Something else happened
        setAddChildError(err.message || 'An unexpected error occurred');
        console.error('Unexpected error:', err.message);
      }
    } finally {
      setAddChildLoading(false);
    }
  };

  // Payment Request QR Code handler
  const handleCreatePaymentRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setPaymentRequestError(null);

    console.log('Payment request debug info:');
    console.log('- selectedChild:', selectedChild);
    console.log('- user:', user);
    console.log('- isAuthenticated:', isAuthenticated);
    console.log('- token:', token ? 'Present' : 'Not present');
    console.log('- paymentRequestForm:', paymentRequestForm);

    if (!paymentRequestForm.amount || !paymentRequestForm.description) {
      setPaymentRequestError('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(paymentRequestForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setPaymentRequestError('Please enter a valid amount');
      return;
    }

    if (!selectedChild) {
      setPaymentRequestError('No child selected');
      return;
    }

    if (!isAuthenticated || !token) {
      setPaymentRequestError('User not authenticated - please log in again');
      return;
    }

    try {
      // Create the payment request JSON
      // For child payment requests, we need to structure the data so the parent can scan it
      // The toAccountNumber should be the child's account (recipient of the money)
      // The parent will be the one sending the money when they scan this QR code
      
      const timestamp = new Date().toISOString();
      const childName = selectedChild.name || `${selectedChild.firstName || ''} ${selectedChild.lastName || ''}`.trim();
      
      const paymentRequestData: PaymentRequestData = {
        url: "localhost:3000",
        webservice: "/payment-request",
        fromAccountId: "child-payment-request", // Identifier that this is a payment request from child
        toAccountNumber: selectedChild.accountNumber || selectedChild.primaryAccountNumber || selectedChild.childUserId.toString(),
        amount: amount,
        description: `${paymentRequestForm.description} (Request from ${childName})`,
        timestamp: timestamp
      };

      console.log('Payment request data:', paymentRequestData);

      // Generate QR code from JSON string
      const qrCodeDataURL = await QRCode.toDataURL(JSON.stringify(paymentRequestData), {
        width: 205, // reduced by 20% from 256
        margin: 2,
        color: {
          dark: branding.textColor,
          light: '#FFFFFF'
        }
      });

      // Add QR code to payment request data
      const paymentRequestWithQR: PaymentRequestData = {
        ...paymentRequestData,
        qrCodeDataURL: qrCodeDataURL
      };

      // Dispatch to Redux store
      dispatch(createPaymentRequest(paymentRequestWithQR));
      
      setShowPaymentRequest(false);
      
      // Reset form
      setPaymentRequestForm({
        amount: '',
        description: ''
      });

    } catch (err: any) {
      console.error('Error generating payment request QR code:', err);
      setPaymentRequestError('Failed to generate QR code: ' + err.message);
    }
  };

  // Cancel payment request
  const handleCancelPaymentRequest = () => {
    dispatch(clearActivePaymentRequest());
    setPaymentRequestForm({
      amount: '',
      description: ''
    });
  };

  const handleDeletePaymentRequest = (idx: number) => {
    dispatch(deletePaymentRequest(idx));
  };

  if (selectedChild) {
    const childData = selectedChild;
    const financialGoals = childData.financialGoals || [];
    const spendingCategories = childData.spendingCategories || [];
    const allowanceDetails = childData.allowanceDetails || { amount: 25, day: 'Friday', lastPayment: '2024-06-01', nextPayment: 'This Friday' };
    const childTransactions = childData.transactions || [];
    const pageSize = 5;
    const pageCount = Math.ceil(childTransactions.length / pageSize);
    const pagedTransactions = childTransactions.slice(page * pageSize, (page + 1) * pageSize);
    const progressPercentage = (childData.balance / (childData.savingsGoal || 1)) * 100;
    return (
      <div style={{ minHeight: '100vh', fontFamily: branding.fontFamily, padding: 0, margin: 0 }}>
        <div
          style={{
            maxWidth: 1200,
            margin: '32px auto',
            padding: '0 16px',
          }}
        >
          {/* Headings Row */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 24 : 32,
            alignItems: isMobile ? 'stretch' : 'flex-end',
            marginBottom: 8,
          }}>
            {/* Left Heading */}
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : 320 }}>
              <h1 style={{ color: branding.textColor, fontWeight: 700, fontSize: 24, margin: 0 }}>Child Wallets</h1>
              {isMobile && (
                <>
                  <button onClick={() => setShowAddChild(true)} style={{ marginTop: 16, marginBottom: 16, padding: '12px 24px', background: branding.primaryColor, color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer', width: '100%' }}>+ Add Child Wallet</button>
                  <div style={{
                    width: '100%',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    marginBottom: 24,
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      background: '#fff',
                      borderRadius: 12
                    }}>
                      <thead>
                        <tr style={{ background: '#f7fafd' }}>
                          <th style={{ padding: 8, textAlign: 'left', color: branding.textColor, fontSize: 12, wordBreak: 'break-word', overflowWrap: 'anywhere', width: '40%', maxWidth: '40%' }}>Name</th>
                          <th style={{ padding: 8, textAlign: 'left', color: branding.textColor, fontSize: 12, wordBreak: 'break-word', overflowWrap: 'anywhere', width: '35%', maxWidth: '35%' }}>Account #</th>
                          <th style={{ padding: 8, textAlign: 'right', color: branding.textColor, fontSize: 12, wordBreak: 'break-word', overflowWrap: 'anywhere', width: '25%', maxWidth: '25%' }}>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {childWallets.map((child, idx) => (
                          <tr
                            key={child.childUserId}
                            style={{
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              background: (selectedChild as ChildWalletData | null)?.childUserId === child.childUserId ? '#eaf6ff' : 'transparent',
                              fontWeight: (selectedChild as ChildWalletData | null)?.childUserId === child.childUserId ? 700 : 400,
                            }}
                            onClick={() => fetchChildProfile(child.childUserId)}
                          >
                            <td style={{ padding: 8, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 12, width: '40%', maxWidth: '40%' }}>{child.firstName} {child.lastName}</td>
                            <td style={{ padding: 8, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 12, width: '35%', maxWidth: '35%' }}>{child.primaryAccountNumber}</td>
                            <td style={{ padding: 8, textAlign: 'right', fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 12, width: '25%', maxWidth: '25%' }}>{branding.currency.symbol}{(child.primaryAccountBalance || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            {/* Right Heading */}
            <div style={{ flex: 2, minWidth: isMobile ? '100%' : 0, display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'space-between', gap: 16 }}>
              <div>
                <h1 style={{ color: branding.textColor, fontWeight: 700, fontSize: 24, margin: 0 }}>Child Profile</h1>
                <div style={{ color: '#888', fontSize: 15, marginTop: 2 }}>
                  {selectedChild ? `Teaching financial responsibility to ${selectedChild.name || ''}` : ''}
                </div>
              </div>
              <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 8 : 12,
                marginLeft: isMobile ? 0 : 'auto',
                marginTop: isMobile ? 16 : 0,
              }}>
                <button
                  onClick={() => setShowAddMoney(true)}
                  style={{
                    padding: '10px 20px',
                    background: branding.primaryColor,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
                    minWidth: isMobile ? '100%' : 'auto',
                  }}
                  disabled={!selectedChild}
                >
                  <span>💰</span> Add Money
                </button>
                <button
                  onClick={() => setShowPaymentRequest(true)}
                  style={{
                    padding: '10px 20px',
                    background: branding.successColor,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: '0 4px 16px rgba(34,197,94,0.2)',
                    minWidth: isMobile ? '100%' : 'auto',
                  }}
                  disabled={!selectedChild}
                >
                  <span>📱</span> Request Payment
                </button>
              </div>
            </div>
          </div>
          {/* Main Panels Row */}
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 24 : 32,
              alignItems: 'flex-start',
            }}
          >
            {/* Table Panel - Only show on desktop */}
            {!isMobile && !isChildUser && (
              <div
                style={{
                  width: isMobile ? '100%' : 360,
                  minWidth: isMobile ? '100%' : 320,
                  maxWidth: isMobile ? '100%' : 400,
                  background: '#fff',
                  borderRadius: 16,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  padding: 24,
                  marginBottom: isMobile ? 24 : 0,
                }}
              >
                <button onClick={() => setShowAddChild(true)} style={{ marginBottom: 24, padding: '12px 24px', background: branding.primaryColor, color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>+ Add Child Wallet</button>
                <div style={{ width: '100%', overflowX: 'visible' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    background: '#fff',
                    borderRadius: 12
                  }}>
                    <thead>
                      <tr style={{ background: '#f7fafd' }}>
                        <th style={{ padding: 16, textAlign: 'left', color: branding.textColor, fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere', width: 120, maxWidth: 120, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>Name</th>
                        <th style={{ padding: 16, textAlign: 'left', color: branding.textColor, fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere', width: 100, maxWidth: 100, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>Account #</th>
                        <th style={{ padding: 16, textAlign: 'right', color: branding.textColor, fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere', width: 70, maxWidth: 70, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {childWallets.map((child, idx) => (
                        <tr key={child.childUserId} style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }} onClick={() => fetchChildProfile(child.childUserId)}>
                          <td style={{ padding: 16, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 13 }}>{child.firstName} {child.lastName}</td>
                          <td style={{ padding: 16, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 13, width: 100, maxWidth: 100, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{child.primaryAccountNumber}</td>
                          <td style={{ padding: 16, textAlign: 'right', fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 13 }}>{branding.currency.symbol}{(child.primaryAccountBalance || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* Profile Panel */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 32 }}>Loading...</div>
              ) : selectedChild ? (
                <div style={{
                  maxWidth: branding.viewport.mainMaxWidth,
                  margin: isMobile ? '0 auto' : '0',
                  padding: isMobile ? '16px' : 0,
                }}>
                  {/* Header */}
                  {/* <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    marginBottom: 24,
                    gap: isMobile ? 16 : 0,
                  }}>
                    <div>
                      <h1 style={{
                        color: branding.textColor,
                        fontWeight: 700,
                        fontSize: isMobile ? 24 : 28,
                        margin: 0,
                        marginBottom: 8,
                      }}>Child Profile</h1>
                    </div>
                  </div> */}

                  {/* Child Profile and Balance */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 20,
                    padding: isMobile ? 20 : 32,
                    marginBottom: 24,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: isMobile ? 24 : 32,
                      alignItems: 'center',
                    }}>
                      {/* Child Profile */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? 16 : 20,
                      }}>
                        <img
                          src={
                            isChildUser
                              ? (profile?.avatarBase64 ? `data:image/jpeg;base64,${profile.avatarBase64}` : undefined)
                              : childData.avatar
                          }
                          alt={childData.name}
                          style={{
                            width: isMobile ? 64 : 80,
                            height: isMobile ? 64 : 80,
                            borderRadius: '50%',
                            border: `3px solid ${branding.primaryColor}`,
                          }}
                        />
                        <div>
                          <h2 style={{
                            margin: 0,
                            marginBottom: 4,
                            color: branding.textColor,
                            fontSize: isMobile ? 20 : 24,
                            fontWeight: 700,
                          }}>{childData.name}</h2>
                          <p style={{
                            margin: 0,
                            color: '#666',
                            fontSize: isMobile ? 14 : 16,
                          }}>Age {childData.age} • {childData.parentName}'s child</p>
                          <p style={{
                            margin: 0,
                            marginTop: 4,
                            color: '#888',
                            fontSize: isMobile ? 12 : 14,
                          }}>Account: {childData.accountNumber}</p>
                        </div>
                      </div>

                      {/* Current Balance */}
                      <div style={{
                        textAlign: isMobile ? 'left' : 'center',
                        padding: isMobile ? '16px 0' : '24px 0',
                        borderTop: isMobile ? '1px solid #eee' : 'none',
                        borderBottom: isMobile ? '1px solid #eee' : 'none',
                      }}>
                        <div style={{
                          fontSize: isMobile ? 14 : 16,
                          color: '#666',
                          marginBottom: 8,
                        }}>Current Balance</div>
                        <div style={{
                          fontSize: isMobile ? 28 : 36,
                          fontWeight: 700,
                          color: branding.primaryColor,
                          marginBottom: 8,
                        }}>{branding.currency.symbol}{(childData.balance ?? 0).toFixed(2)}</div>
                        <div style={{
                          fontSize: isMobile ? 12 : 14,
                          color: '#888',
                        }}>Available to spend</div>
                      </div>

                      {/* Savings Goal Progress */}
                      <div style={{
                        textAlign: isMobile ? 'left' : 'center',
                      }}>
                        <div style={{
                          fontSize: isMobile ? 14 : 16,
                          color: '#666',
                          marginBottom: 8,
                        }}>Savings Goal</div>
                        <div style={{
                          fontSize: isMobile ? 20 : 24,
                          fontWeight: 700,
                          color: branding.successColor,
                          marginBottom: 12,
                        }}>${(childData.savingsGoal ?? 0).toFixed(2)}</div>
                        <div style={{
                          width: '100%',
                          height: 8,
                          background: '#eee',
                          borderRadius: 4,
                          overflow: 'hidden',
                          marginBottom: 8,
                        }}>
                          <div style={{
                            width: `${Math.min(progressPercentage, 100)}%`,
                            height: '100%',
                            background: branding.successColor,
                            borderRadius: 4,
                            transition: 'width 0.3s',
                          }} />
                        </div>
                        <div style={{
                          fontSize: isMobile ? 12 : 14,
                          color: '#888',
                        }}>{progressPercentage.toFixed(1)}% complete</div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Request QR Code and History - Responsive Row */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? 24 : 32,
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      marginBottom: 24
                    }}
                  >
                    {/* Active Payment Request QR Code */}
                    {activePaymentRequest?.qrCodeDataURL && (
                      <div style={{
                        flex: isMobile ? 'unset' : '0 0 320px',
                        background: '#fff',
                        borderRadius: 20,
                        padding: isMobile ? 20 : 24,
                        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                        textAlign: 'center',
                        marginBottom: isMobile ? 24 : 0,
                        minWidth: isMobile ? 'unset' : 260,
                        maxWidth: isMobile ? 'unset' : 380
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 16,
                          gap: 12
                        }}>
                          <h3 style={{
                            margin: 0,
                            color: branding.textColor,
                            fontSize: isMobile ? 15 : 16,
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}>Payment Request QR Code</h3>
                          <button
                            onClick={handleCancelPaymentRequest}
                            style={{
                              padding: isMobile ? '2px 8px' : '4px 10px',
                              background: branding.errorColor,
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              fontSize: isMobile ? 13 : 15,
                              fontWeight: 500,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0,
                              minWidth: 0,
                              lineHeight: 1.1,
                            }}
                          >
                            <span style={{ fontSize: isMobile ? 15 : 18 }}>✕</span>
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                          <img
                            src={activePaymentRequest.qrCodeDataURL}
                            alt="Payment Request QR Code"
                            style={{
                              width: isMobile ? 128 : 160,
                              height: isMobile ? 128 : 160,
                              border: '2px solid #eee',
                              borderRadius: 12,
                              background: '#fff',
                            }}
                          />
                          <div style={{
                            color: '#666',
                            fontSize: isMobile ? 14 : 15,
                            maxWidth: 300,
                            lineHeight: 1.5,
                          }}>
                            Show this QR code to request payment from your parent. 
                            They can scan it with their banking app to send you money.
                          </div>
                          <div style={{
                            background: '#f7fafd',
                            padding: 12,
                            borderRadius: 8,
                            fontSize: 14,
                            color: branding.textColor,
                            fontWeight: 600,
                          }}>
                            Amount: ${activePaymentRequest.amount.toFixed(2)}
                          </div>
                          <div style={{
                            background: '#f7fafd',
                            padding: 12,
                            borderRadius: 8,
                            fontSize: 14,
                            color: '#666',
                            textAlign: 'center',
                            maxWidth: 220,
                          }}>
                            {activePaymentRequest.description}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Previous Payment Requests */}
                    {paymentRequestHistory && paymentRequestHistory.length > 0 && (
                      <div style={{
                        flex: isMobile ? 'unset' : '1',
                        background: '#fff',
                        borderRadius: 20,
                        padding: isMobile ? 20 : 24,
                        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                        minWidth: isMobile ? 'unset' : 220,
                        maxWidth: isMobile ? 'unset' : 420,
                        marginLeft: isMobile ? 0 : 0
                      }}>
                        <h3 style={{
                          margin: 0,
                          marginBottom: 12,
                          color: branding.textColor,
                          fontSize: isMobile ? 16 : 18,
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>Previous Payment Requests</h3>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 12,
                          justifyContent: 'center',
                          marginBottom: 0
                        }}>
                          {paymentRequestHistory.map((req, idx) => (
                            <div key={idx} style={{ textAlign: 'center', position: 'relative', width: isMobile ? 128 : 112 }}>
                              <button
                                onClick={() => handleDeletePaymentRequest(idx)}
                                style={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  background: 'rgba(0,0,0,0.45)',
                                  border: 'none',
                                  cursor: 'pointer',
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  zIndex: 2
                                }}
                                title="Delete QR code"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 6h18M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                  <path d="M10 11v6M14 11v6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <img
                                src={req.qrCodeDataURL}
                                alt={`Payment Request QR Code #${idx + 1}`}
                                style={{
                                  width: isMobile ? 128 : 112,
                                  height: isMobile ? 128 : 112,
                                  border: '2px solid #eee',
                                  borderRadius: 12,
                                  marginBottom: 6,
                                  background: '#fff',
                                }}
                              />
                              <div style={{ fontSize: 11, color: '#666' }}>
                                Amount: ${req.amount?.toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Financial Goals */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 20,
                    padding: isMobile ? 20 : 32,
                    marginBottom: 24,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  }}>
                    <h3 style={{
                      margin: 0,
                      marginBottom: isMobile ? 16 : 24,
                      color: branding.textColor,
                      fontSize: isMobile ? 18 : 20,
                      fontWeight: 600,
                    }}>Financial Goals</h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: isMobile ? 16 : 20,
                    }}>
                      {financialGoals.map((goal: any, index: number) => {
                        const goalProgress = (goal.current / goal.target) * 100;
                        return (
                          <div key={index} style={{
                            padding: isMobile ? 16 : 20,
                            border: '1.5px solid #eee',
                            borderRadius: 16,
                            background: '#f7fafd',
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              marginBottom: 16,
                            }}>
                              <span style={{ fontSize: isMobile ? 20 : 24 }}>{goal.icon}</span>
                              <div>
                                <div style={{
                                  fontWeight: 600,
                                  color: branding.textColor,
                                  fontSize: isMobile ? 14 : 16,
                                }}>{goal.name}</div>
                                <div style={{
                                  color: '#666',
                                  fontSize: isMobile ? 12 : 14,
                                }}>${goal.current.toFixed(2)} / ${goal.target.toFixed(2)}</div>
                              </div>
                            </div>
                            <div style={{
                              width: '100%',
                              height: 6,
                              background: '#eee',
                              borderRadius: 3,
                              overflow: 'hidden',
                              marginBottom: 8,
                            }}>
                              <div style={{
                                width: `${Math.min(goalProgress, 100)}%`,
                                height: '100%',
                                background: branding.primaryColor,
                                borderRadius: 3,
                                transition: 'width 0.3s',
                              }} />
                            </div>
                            <div style={{
                              fontSize: isMobile ? 11 : 13,
                              color: '#888',
                              textAlign: 'center',
                            }}>{goalProgress.toFixed(1)}% complete</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Spending Analytics */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: isMobile ? 24 : 32,
                    marginBottom: 24,
                  }}>
                    {/* Spending by Category */}
                    <div style={{
                      background: '#fff',
                      borderRadius: 20,
                      padding: isMobile ? 20 : 32,
                      boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    }}>
                      <h3 style={{
                        margin: 0,
                        marginBottom: isMobile ? 16 : 24,
                        color: branding.textColor,
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 600,
                      }}>Spending by Category</h3>
                      <div style={{ display: 'grid', gap: isMobile ? 12 : 16 }}>
                        {spendingCategories.map((cat: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                          }}>
                            <div style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: cat.color,
                            }} />
                            <div style={{ flex: 1 }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 4,
                              }}>
                                <span style={{
                                  fontWeight: 500,
                                  color: branding.textColor,
                                  fontSize: isMobile ? 13 : 14,
                                }}>{cat.category}</span>
                                <span style={{
                                  fontWeight: 600,
                                  color: branding.textColor,
                                  fontSize: isMobile ? 13 : 14,
                                }}>${cat.amount.toFixed(2)}</span>
                              </div>
                              <div style={{
                                width: '100%',
                                height: 4,
                                background: '#eee',
                                borderRadius: 2,
                                overflow: 'hidden',
                              }}>
                                <div style={{
                                  width: `${cat.percentage}%`,
                                  height: '100%',
                                  background: cat.color,
                                  borderRadius: 2,
                                }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Allowance Info */}
                    <div style={{
                      background: '#fff',
                      borderRadius: 20,
                      padding: isMobile ? 20 : 32,
                      boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    }}>
                      <h3 style={{
                        margin: 0,
                        marginBottom: isMobile ? 16 : 24,
                        color: branding.textColor,
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 600,
                      }}>Allowance Details</h3>
                      <div style={{
                        background: branding.primaryColor,
                        color: '#fff',
                        padding: isMobile ? 20 : 24,
                        borderRadius: 16,
                        textAlign: 'center',
                        marginBottom: 20,
                      }}>
                        <div style={{ fontSize: isMobile ? 12 : 14, opacity: 0.9, marginBottom: 8 }}>Weekly Allowance</div>
                        <div style={{ fontSize: isMobile ? 28 : 32, fontWeight: 700, marginBottom: 8 }}>{branding.currency.symbol}{(childData.allowance ?? 0).toFixed(2)}</div>
                        <div style={{ fontSize: isMobile ? 12 : 14, opacity: 0.9 }}>Paid every {childData.allowanceDay}</div>
                      </div>
                      <div style={{
                        padding: isMobile ? 12 : 16,
                        background: '#f7fafd',
                        borderRadius: 12,
                        border: '1px solid #e3eaf5',
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 8,
                          fontSize: isMobile ? 12 : 14,
                        }}>
                          <span style={{ color: '#666' }}>Last payment:</span>
                          <span style={{ fontWeight: 600, color: branding.textColor }}>{childData.lastAllowance}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: isMobile ? 12 : 14,
                        }}>
                          <span style={{ color: '#666' }}>Next payment:</span>
                          <span style={{ fontWeight: 600, color: branding.textColor }}>This {childData.allowanceDay}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 20,
                    padding: isMobile ? 20 : 32,
                    marginBottom: 24,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  }}>
                    <h3 style={{
                      margin: 0,
                      marginBottom: isMobile ? 16 : 24,
                      color: branding.textColor,
                      fontSize: isMobile ? 18 : 20,
                      fontWeight: 600,
                    }}>Recent Transactions</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 14 : 16 }}>
                        <tbody>
                          {(childData?.transactions || []).map((tx: any, index: number) => (
                            <React.Fragment key={tx.id}>
                              <tr
                                style={{
                                  background: expanded === tx.id ? '#f7fafd' : 'transparent',
                                  cursor: 'pointer',
                                  borderRadius: expanded === tx.id ? 10 : 0,
                                  transition: 'background 0.2s',
                                }}
                                onClick={() => setExpanded(expanded === tx.id ? null : tx.id)}
                              >
                                <td style={{ 
                                  padding: isMobile ? '8px 12px' : 12, 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  fontWeight: 600,
                                  fontSize: isMobile ? 13 : 14,
                                }}>
                                  <span style={{
                                    background: '#f2f2f7',
                                    borderRadius: '50%',
                                    width: isMobile ? 32 : 36,
                                    height: isMobile ? 32 : 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: isMobile ? 8 : 12,
                                    fontSize: isMobile ? 16 : 18,
                                  }}>{tx.icon}</span>
                                  {tx.title}
                                </td>
                                <td style={{ 
                                  padding: isMobile ? '8px 12px' : 12, 
                                  color: tx.amount < 0 ? branding.errorColor : branding.successColor, 
                                  fontWeight: 600, 
                                  textAlign: 'right',
                                  fontSize: isMobile ? 13 : 14,
                                  whiteSpace: 'nowrap',
                                }}>
                                  {tx.amount < 0 ? '-' : '+'}${Math.abs(tx.amount).toFixed(2)}
                                </td>
                              </tr>
                              {expanded === tx.id && (
                                <tr style={{ background: '#eaf6ff' }}>
                                  <td colSpan={2} style={{ padding: 0, borderBottom: '1px solid #e0e0e0', borderRadius: 10 }}>
                                    <div style={{ overflowX: 'auto' }}>
                                      <table style={{ 
                                        width: '100%', 
                                        borderCollapse: 'collapse', 
                                        fontSize: isMobile ? 13 : 15, 
                                        background: 'transparent',
                                        minWidth: isMobile ? 500 : 'auto',
                                      }}>
                                        <thead>
                                          <tr>
                                            <th style={{ padding: isMobile ? 8 : 10, textAlign: 'left', color: branding.textColor }}>Date</th>
                                            <th style={{ padding: isMobile ? 8 : 10, textAlign: 'left', color: branding.textColor }}>Category</th>
                                            <th style={{ padding: isMobile ? 8 : 10, textAlign: 'left', color: branding.textColor }}>Status</th>
                                            <th style={{ padding: isMobile ? 8 : 10, textAlign: 'left', color: branding.textColor }}>Details</th>
                                            <th style={{ padding: isMobile ? 8 : 10, textAlign: 'right', color: branding.textColor }}>Amount</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr>
                                            <td style={{ padding: isMobile ? 8 : 10, whiteSpace: 'nowrap' }}>{tx.date}</td>
                                            <td style={{ padding: isMobile ? 8 : 10 }}>{tx.category}</td>
                                            <td style={{ padding: isMobile ? 8 : 10 }}>{tx.status}</td>
                                            <td style={{ padding: isMobile ? 8 : 10 }}>{tx.details}</td>
                                            <td style={{ 
                                              padding: isMobile ? 8 : 10, 
                                              color: tx.amount < 0 ? branding.errorColor : branding.successColor, 
                                              fontWeight: 600, 
                                              textAlign: 'right',
                                              whiteSpace: 'nowrap',
                                            }}>
                                              {tx.amount < 0 ? '-' : '+'}${Math.abs(tx.amount).toFixed(2)}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      marginTop: isMobile ? 16 : 24, 
                      gap: 8 
                    }}>
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0}
                        style={{
                          fontSize: isMobile ? 18 : 22,
                          width: isMobile ? 32 : 36,
                          height: isMobile ? 32 : 36,
                          minWidth: 0,
                          padding: 0,
                          borderRadius: '50%',
                          border: page === 0 ? '1.5px solid #eee' : '1.5px solid #8e44ad',
                          background: 'transparent',
                          color: page === 0 ? '#bbb' : '#8e44ad',
                          cursor: page === 0 ? 'not-allowed' : 'pointer',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'border 0.2s, color 0.2s',
                        }}
                        aria-label="Previous page"
                      >
                        {'<'}
                      </button>
                      <span style={{ 
                        fontWeight: 700, 
                        color: '#8e44ad', 
                        fontSize: isMobile ? 15 : 17, 
                        minWidth: isMobile ? 50 : 60, 
                        textAlign: 'center', 
                        letterSpacing: 1 
                      }}>
                        {page + 1} <span style={{ fontWeight: 400, fontSize: isMobile ? 13 : 15, color: '#888' }}>/ {pageCount}</span>
                      </span>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page === pageCount - 1}
                        style={{
                          fontSize: isMobile ? 18 : 22,
                          width: isMobile ? 32 : 36,
                          height: isMobile ? 32 : 36,
                          minWidth: 0,
                          padding: 0,
                          borderRadius: '50%',
                          border: page === pageCount - 1 ? '1.5px solid #eee' : '1.5px solid #8e44ad',
                          background: 'transparent',
                          color: page === pageCount - 1 ? '#bbb' : '#8e44ad',
                          cursor: page === pageCount - 1 ? 'not-allowed' : 'pointer',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'border 0.2s, color 0.2s',
                        }}
                        aria-label="Next page"
                      >
                        {'>'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#888', padding: 32 }}>Select a child to view their wallet.</div>
              )}
            </div>
          </div>
        </div>
        
        {/* Payment Request Modal */}
        {showPaymentRequest && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 24, padding: 0, maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '24px 32px 0 32px', borderTopLeftRadius: 24, borderTopRightRadius: 24 }}>
                <h3 style={{ margin: 0, marginBottom: 8, color: branding.textColor, fontSize: 22, fontWeight: 700 }}>Request Payment</h3>
                <p style={{ margin: 0, marginBottom: 16, color: '#888', fontSize: 14 }}>Enter the amount and description for your payment request.</p>
              </div>
              <form onSubmit={handleCreatePaymentRequest} id="payment-request-form" style={{ padding: '0 32px 0 32px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left' }}>
                    <label style={{ fontSize: 14, color: '#444', fontWeight: 600 }}>Amount ({branding.currency.symbol})</label>
                    <input 
                      type="number" 
                      step="0.01"
                      min="0.01"
                      placeholder="0.00" 
                      value={paymentRequestForm.amount} 
                      onChange={e => setPaymentRequestForm(f => ({ ...f, amount: e.target.value }))} 
                      style={{ 
                        padding: 12, 
                        borderRadius: 8, 
                        border: '1.5px solid #eee', 
                        fontSize: 16,
                        textAlign: 'center',
                        fontWeight: 600,
                      }} 
                      required 
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left' }}>
                    <label style={{ fontSize: 14, color: '#444', fontWeight: 600 }}>Description</label>
                    <textarea 
                      placeholder="What is this payment for?" 
                      value={paymentRequestForm.description} 
                      onChange={e => setPaymentRequestForm(f => ({ ...f, description: e.target.value }))} 
                      style={{ 
                        padding: 12, 
                        borderRadius: 8, 
                        border: '1.5px solid #eee', 
                        fontSize: 15,
                        resize: 'vertical',
                        minHeight: 80,
                        fontFamily: 'inherit',
                      }} 
                      required 
                    />
                  </div>
                </div>
                {/* Error Display */}
                {paymentRequestError && (
                  <div style={{ 
                    background: '#fee', 
                    border: '1px solid #fcc', 
                    borderRadius: 8, 
                    padding: 12, 
                    marginBottom: 16, 
                    color: '#c33',
                    fontSize: 14,
                    textAlign: 'left'
                  }}>
                    <strong>Error:</strong> {paymentRequestError}
                  </div>
                )}
              </form>
              <div style={{ display: 'flex', gap: 16, padding: '16px 32px 24px 32px', borderBottomLeftRadius: 24, borderBottomRightRadius: 24, background: '#fff' }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowPaymentRequest(false);
                    setPaymentRequestError(null);
                    setPaymentRequestForm({ amount: '', description: '' });
                  }} 
                  style={{ flex: 1, padding: '12px 0', background: '#fff', border: '1.5px solid #eee', borderRadius: 12, fontSize: 15, fontWeight: 600, color: '#666', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  form="payment-request-form"
                  style={{ 
                    flex: 1, 
                    padding: '12px 0', 
                    background: branding.successColor, 
                    border: 'none', 
                    borderRadius: 12, 
                    fontSize: 15, 
                    fontWeight: 600, 
                    color: '#fff', 
                    cursor: 'pointer'
                  }}
                >
                  Generate QR Code
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Child Modal */}
        {showAddChild && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 24, padding: 0, maxWidth: 540, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', textAlign: 'center', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
              <div style={{ padding: '24px 32px 0 32px', borderTopLeftRadius: 24, borderTopRightRadius: 24 }}>
                <h3 style={{ margin: 0, marginBottom: 8, color: branding.textColor, fontSize: 22, fontWeight: 700 }}>Add Child Wallet</h3>
                <p style={{ margin: 0, marginBottom: 16, color: '#888', fontSize: 14 }}>Fill in the details below to create a new child wallet.</p>
              </div>
              <form onSubmit={handleAddChild} id="add-child-form" style={{ flex: 1, overflowY: 'auto', padding: '0 32px 0 32px' }}>
                {/* Personal Info */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>First Name</label>
                    <input type="text" placeholder="First Name" value={addChildForm.firstName} onChange={e => setAddChildForm(f => ({ ...f, firstName: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Last Name</label>
                    <input type="text" placeholder="Last Name" value={addChildForm.lastName} onChange={e => setAddChildForm(f => ({ ...f, lastName: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: isMobile ? '1' : '1 / span 2' }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Username</label>
                    <input type="text" placeholder="Username" value={addChildForm.username} onChange={e => setAddChildForm(f => ({ ...f, username: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: isMobile ? '1' : '1 / span 2' }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Password</label>
                    <input type="password" placeholder="Password" value={addChildForm.password} onChange={e => setAddChildForm(f => ({ ...f, password: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                </div>
                {/* Account Info */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>IBAN</label>
                    <input type="text" placeholder="IBAN" value={addChildForm.iban} onChange={e => setAddChildForm(f => ({ ...f, iban: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Encrypted Card Number</label>
                    <input type="text" placeholder="Encrypted Card Number" value={addChildForm.encryptedCardNumber} onChange={e => setAddChildForm(f => ({ ...f, encryptedCardNumber: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Activation Date</label>
                    <input type="date" placeholder="Activation Date" value={addChildForm.activationDate} onChange={e => setAddChildForm(f => ({ ...f, activationDate: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Date of Birth</label>
                    <input type="date" placeholder="Date of Birth" value={addChildForm.dob} onChange={e => setAddChildForm(f => ({ ...f, dob: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                </div>
                {/* Contact Info */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Mobile</label>
                    <input type="text" placeholder="Mobile" value={addChildForm.mobile} onChange={e => setAddChildForm(f => ({ ...f, mobile: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Email</label>
                    <input type="email" placeholder="Email" value={addChildForm.email} onChange={e => setAddChildForm(f => ({ ...f, email: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: isMobile ? '1' : '1 / span 2' }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Status</label>
                    <input type="text" placeholder="Status" value={addChildForm.status} onChange={e => setAddChildForm(f => ({ ...f, status: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                </div>
                {/* Address Info */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Address Line 1</label>
                    <input type="text" placeholder="Address Line 1" value={addChildForm.add1} onChange={e => setAddChildForm(f => ({ ...f, add1: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Address Line 2</label>
                    <input type="text" placeholder="Address Line 2" value={addChildForm.add2} onChange={e => setAddChildForm(f => ({ ...f, add2: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>State</label>
                    <input type="text" placeholder="State" value={addChildForm.state} onChange={e => setAddChildForm(f => ({ ...f, state: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Postcode</label>
                    <input type="text" placeholder="Postcode" value={addChildForm.postcode} onChange={e => setAddChildForm(f => ({ ...f, postcode: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: isMobile ? '1' : '1 / span 2' }}>
                    <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Country</label>
                    <input type="text" placeholder="Country" value={addChildForm.country} onChange={e => setAddChildForm(f => ({ ...f, country: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                  </div>
                </div>
                {/* Error Display */}
                {addChildError && (
                  <div style={{ 
                    background: '#fee', 
                    border: '1px solid #fcc', 
                    borderRadius: 8, 
                    padding: 12, 
                    marginBottom: 16, 
                    color: '#c33',
                    fontSize: 14,
                    textAlign: 'left'
                  }}>
                    <strong>Error:</strong> {addChildError}
                  </div>
                )}
              </form>
              <div style={{ display: 'flex', gap: 16, padding: '16px 32px 24px 32px', borderBottomLeftRadius: 24, borderBottomRightRadius: 24, background: '#fff', position: 'sticky', bottom: 0, zIndex: 2 }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAddChild(false);
                    setAddChildError(null);
                  }} 
                  style={{ flex: 1, padding: '12px 0', background: '#fff', border: '1.5px solid #eee', borderRadius: 12, fontSize: 15, fontWeight: 600, color: '#666', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  form="add-child-form"
                  disabled={addChildLoading} 
                  style={{ 
                    flex: 1, 
                    padding: '12px 0', 
                    background: addChildLoading ? '#ccc' : branding.primaryColor, 
                    border: 'none', 
                    borderRadius: 12, 
                    fontSize: 15, 
                    fontWeight: 600, 
                    color: '#fff', 
                    cursor: addChildLoading ? 'not-allowed' : 'pointer',
                    opacity: addChildLoading ? 0.7 : 1 
                  }}
                >
                  {addChildLoading ? 'Creating...' : 'Add Child'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Otherwise, show the table of child wallets and add button
  return (
    <div style={{ minHeight: '100vh', fontFamily: branding.fontFamily, padding: 0, margin: 0 }}>
      <div style={{ maxWidth: branding.viewport.mainMaxWidth, margin: '32px auto', padding: 0 }}>
        <h1 style={{ color: branding.textColor, fontWeight: 700, fontSize: 28, marginBottom: 24 }}>Child Wallets</h1>
        <button onClick={() => setShowAddChild(true)} style={{ marginBottom: 24, padding: '12px 24px', background: branding.primaryColor, color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>+ Add Child Wallet</button>
        {isMobile ? (
          <div style={{
            width: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            marginBottom: 24,
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: '#fff',
              borderRadius: 12
            }}>
              <thead>
                <tr style={{ background: '#f7fafd' }}>
                  <th style={{ padding: 8, textAlign: 'left', color: branding.textColor, fontSize: 12, wordBreak: 'break-word', overflowWrap: 'anywhere', width: '40%', maxWidth: '40%' }}>Name</th>
                  <th style={{ padding: 8, textAlign: 'left', color: branding.textColor, fontSize: 12, wordBreak: 'break-word', overflowWrap: 'anywhere', width: '35%', maxWidth: '35%' }}>Account #</th>
                  <th style={{ padding: 8, textAlign: 'right', color: branding.textColor, fontSize: 12, wordBreak: 'break-word', overflowWrap: 'anywhere', width: '25%', maxWidth: '25%' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {childWallets.map((child, idx) => (
                  <tr
                    key={child.childUserId}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      background: (selectedChild as ChildWalletData | null)?.childUserId === child.childUserId ? '#eaf6ff' : 'transparent',
                      fontWeight: (selectedChild as ChildWalletData | null)?.childUserId === child.childUserId ? 700 : 400,
                    }}
                    onClick={() => fetchChildProfile(child.childUserId)}
                  >
                    <td style={{ padding: 8, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 12, width: '40%', maxWidth: '40%' }}>{child.firstName} {child.lastName}</td>
                    <td style={{ padding: 8, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 12, width: '35%', maxWidth: '35%' }}>{child.primaryAccountNumber}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 12, width: '25%', maxWidth: '25%' }}>{branding.currency.symbol}{(child.primaryAccountBalance || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ width: '100%', overflowX: 'visible' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: '#fff',
              borderRadius: 12
            }}>
              <thead>
                <tr style={{ background: '#f7fafd' }}>
                  <th style={{ padding: isMobile ? 8 : 16, textAlign: 'left', color: branding.textColor, fontSize: isMobile ? 12 : 13, wordBreak: 'break-word', overflowWrap: 'anywhere', width: 120, maxWidth: 120, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>Name</th>
                  <th style={{ padding: isMobile ? 8 : 16, textAlign: 'left', color: branding.textColor, fontSize: isMobile ? 12 : 13, wordBreak: 'break-word', overflowWrap: 'anywhere', width: 100, maxWidth: 100, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>Account #</th>
                  <th style={{ padding: isMobile ? 8 : 16, textAlign: 'right', color: branding.textColor, fontSize: isMobile ? 12 : 13, wordBreak: 'break-word', overflowWrap: 'anywhere', width: 70, maxWidth: 70, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {childWallets.map((child, idx) => (
                  <tr key={child.childUserId} style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }} onClick={() => fetchChildProfile(child.childUserId)}>
                    <td style={{ padding: isMobile ? 8 : 16, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: isMobile ? 12 : 13 }}>{child.firstName} {child.lastName}</td>
                    <td style={{ padding: isMobile ? 8 : 16, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: isMobile ? 12 : 13, width: 100, maxWidth: 100, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{child.primaryAccountNumber}</td>
                    <td style={{ padding: isMobile ? 8 : 16, textAlign: 'right', fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: isMobile ? 12 : 13 }}>{branding.currency.symbol}{(child.primaryAccountBalance || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Add Child Modal */}
      {showAddChild && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 24, padding: 0, maxWidth: 540, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', textAlign: 'center', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ padding: '24px 32px 0 32px', borderTopLeftRadius: 24, borderTopRightRadius: 24 }}>
              <h3 style={{ margin: 0, marginBottom: 8, color: branding.textColor, fontSize: 22, fontWeight: 700 }}>Add Child Wallet</h3>
              <p style={{ margin: 0, marginBottom: 16, color: '#888', fontSize: 14 }}>Fill in the details below to create a new child wallet.</p>
            </div>
            <form onSubmit={handleAddChild} id="add-child-form" style={{ flex: 1, overflowY: 'auto', padding: '0 32px 0 32px' }}>
              {/* Personal Info */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>First Name</label>
                  <input type="text" placeholder="First Name" value={addChildForm.firstName} onChange={e => setAddChildForm(f => ({ ...f, firstName: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Last Name</label>
                  <input type="text" placeholder="Last Name" value={addChildForm.lastName} onChange={e => setAddChildForm(f => ({ ...f, lastName: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: isMobile ? '1' : '1 / span 2' }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Username</label>
                  <input type="text" placeholder="Username" value={addChildForm.username} onChange={e => setAddChildForm(f => ({ ...f, username: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: isMobile ? '1' : '1 / span 2' }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Password</label>
                  <input type="password" placeholder="Password" value={addChildForm.password} onChange={e => setAddChildForm(f => ({ ...f, password: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
              </div>
              {/* Account Info */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>IBAN</label>
                  <input type="text" placeholder="IBAN" value={addChildForm.iban} onChange={e => setAddChildForm(f => ({ ...f, iban: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Encrypted Card Number</label>
                  <input type="text" placeholder="Encrypted Card Number" value={addChildForm.encryptedCardNumber} onChange={e => setAddChildForm(f => ({ ...f, encryptedCardNumber: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Activation Date</label>
                  <input type="date" placeholder="Activation Date" value={addChildForm.activationDate} onChange={e => setAddChildForm(f => ({ ...f, activationDate: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Date of Birth</label>
                  <input type="date" placeholder="Date of Birth" value={addChildForm.dob} onChange={e => setAddChildForm(f => ({ ...f, dob: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
              </div>
              {/* Contact Info */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Mobile</label>
                  <input type="text" placeholder="Mobile" value={addChildForm.mobile} onChange={e => setAddChildForm(f => ({ ...f, mobile: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Email</label>
                  <input type="email" placeholder="Email" value={addChildForm.email} onChange={e => setAddChildForm(f => ({ ...f, email: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: isMobile ? '1' : '1 / span 2' }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Status</label>
                  <input type="text" placeholder="Status" value={addChildForm.status} onChange={e => setAddChildForm(f => ({ ...f, status: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
              </div>
              {/* Address Info */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Address Line 1</label>
                  <input type="text" placeholder="Address Line 1" value={addChildForm.add1} onChange={e => setAddChildForm(f => ({ ...f, add1: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Address Line 2</label>
                  <input type="text" placeholder="Address Line 2" value={addChildForm.add2} onChange={e => setAddChildForm(f => ({ ...f, add2: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>State</label>
                  <input type="text" placeholder="State" value={addChildForm.state} onChange={e => setAddChildForm(f => ({ ...f, state: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Postcode</label>
                  <input type="text" placeholder="Postcode" value={addChildForm.postcode} onChange={e => setAddChildForm(f => ({ ...f, postcode: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: isMobile ? '1' : '1 / span 2' }}>
                  <label style={{ fontSize: 13, color: '#444', textAlign: 'left' }}>Country</label>
                  <input type="text" placeholder="Country" value={addChildForm.country} onChange={e => setAddChildForm(f => ({ ...f, country: e.target.value }))} style={{ padding: 10, borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }} required />
                </div>
              </div>
              {/* Error Display */}
              {addChildError && (
                <div style={{ 
                  background: '#fee', 
                  border: '1px solid #fcc', 
                  borderRadius: 8, 
                  padding: 12, 
                  marginBottom: 16, 
                  color: '#c33',
                  fontSize: 14,
                  textAlign: 'left'
                }}>
                  <strong>Error:</strong> {addChildError}
                </div>
              )}
            </form>
            <div style={{ display: 'flex', gap: 16, padding: '16px 32px 24px 32px', borderBottomLeftRadius: 24, borderBottomRightRadius: 24, background: '#fff', position: 'sticky', bottom: 0, zIndex: 2 }}>
              <button 
                type="button" 
                onClick={() => {
                  setShowAddChild(false);
                  setAddChildError(null);
                }} 
                style={{ flex: 1, padding: '12px 0', background: '#fff', border: '1.5px solid #eee', borderRadius: 12, fontSize: 15, fontWeight: 600, color: '#666', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                form="add-child-form"
                disabled={addChildLoading} 
                style={{ 
                  flex: 1, 
                  padding: '12px 0', 
                  background: addChildLoading ? '#ccc' : branding.primaryColor, 
                  border: 'none', 
                  borderRadius: 12, 
                  fontSize: 15, 
                  fontWeight: 600, 
                  color: '#fff', 
                  cursor: addChildLoading ? 'not-allowed' : 'pointer',
                  opacity: addChildLoading ? 0.7 : 1 
                }}
              >
                {addChildLoading ? 'Creating...' : 'Add Child'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildWallet; 